import { resultTone } from '../../lib/resultTone';
import type { ResultTone } from '../../lib/resultTone';
import type { ProbeOutcome } from '../../lib/types';

/**
 * The one place a result's tone becomes a colour.
 *
 * `resultTone` in `src/lib` decides *what a row is* — alive, dead, or the third
 * answer this platform needs, `unmeasurable`. That classification is data and is
 * tested there. What is left is presentation, and it is exactly one mapping: the
 * `.c-*` utilities in `components.css`, which set both `color` and the
 * `--value-color` custom property the themed blocks read.
 *
 * It lives here rather than in either component because it was the *colour* that
 * had drifted before: the rule was written out twice, in the dashboard's compact
 * row and in the results list's tile, each with a comment asking the next person
 * to keep the copies identical. The same link showing amber in one list and red
 * in the other reads as a bug in the data rather than as two views of one row.
 *
 * `c-warn`, never `c-dead`, for a mixed-content row. A red dot in a list is a
 * verdict, and Safari's refusal to make a plain `http://` request is a property
 * of the browser, not of the link (SPEC §7.3, §14 #3) — which is what
 * `ios_status_unmeasurable` says in words on the same row's tile.
 */
const TONE_CLASS: Record<ResultTone, string> = {
  alive: 'c-alive',
  dead: 'c-dead',
  unmeasurable: 'c-warn',
};

/** Takes any probe outcome, so a `CheckResult` fits without unwrapping. */
export function toneClass(result: ProbeOutcome): string {
  return TONE_CLASS[resultTone(result)];
}
